"use client";

import React, { useState, useEffect, useMemo } from "react";
import type { FeedbackItem, FeedbackStatus, Comment, UserInfo } from "../types";
import { useMarkupStore } from "../store";
import {
    formatDate,
    generateId,
    cn,
    commentMentions,
    resolveMentions,
    splitMentions,
} from "../utils";
import {
    ArrowLeftIcon,
    ArchiveIcon,
    TrashIcon,
    SendIcon,
    UserIcon,
    ZoomInIcon,
    CloseIcon,
    CheckIcon,
    EditIcon,
    CopyIcon,
    DesktopIcon,
    TabletIcon,
    MobileIcon,
} from "./Icons";
import {
    updateFeedback as updateFirebaseFeedback,
    deleteFeedback as deleteFirebaseFeedback,
    archiveFeedback as archiveFirebaseFeedback,
    addFeedbackComment,
    assignFeedback,
    setFeedbackDueDate,
    storeFeedbackAssets,
} from "../services/firestore";
import {
    canDeleteFeedback,
    canEditFeedback,
    canManageWorkflow,
    listTeamMembers,
} from "../services/userProfile";
import { buildTicketLink } from "../utils/shareLink";
import type { FeedbackExtras } from "./FeedbackForm";
import { FeedbackForm } from "./FeedbackForm";
import { CommentInput } from "./CommentInput";

interface FeedbackDetailProps {
    feedback: FeedbackItem;
    onBack: () => void;
    onDelete?: (id: string) => void;
    onUpdate?: (feedback: FeedbackItem) => void;
}

// One fetch per page load for the assignee picker.
let teamMembersRequest: Promise<UserInfo[]> | null = null;
const loadTeamMembers = () => {
    teamMembersRequest ??= listTeamMembers().catch((error) => {
        teamMembersRequest = null;
        throw error;
    });
    return teamMembersRequest;
};

const statusOptions: { value: FeedbackStatus; label: string; color: string }[] =
    [
        { value: "open", label: "Open", color: "bg-matter-ink/10 text-gray-900" },
        {
            value: "in-progress",
            label: "In Progress",
            color: "bg-yellow-100 text-gray-900",
        },
        {
            value: "resolved",
            label: "Resolved",
            color: "bg-matter-ink text-matter-cream",
        },
        {
            value: "closed",
            label: "Closed",
            color: "bg-matter-accent/15 text-gray-900",
        },
    ];

const priorityColors: Record<string, string> = {
    low: "bg-matter-ink/5 text-gray-700",
    medium: "bg-matter-ink/10 text-gray-800",
    high: "bg-matter-accent/15 text-gray-800",
    critical: "bg-matter-accent text-white",
};

import { WidgetPortal } from "./WidgetPortal";

/** `2026-10-03` → `3 Oct 2026`, in the reader's locale, without a timezone shift. */
const formatDueDate = (value: string) => {
    const [y, m, d] = value.split("-").map(Number);
    return y && m && d
        ? new Date(y, m - 1, d).toLocaleDateString(undefined, {
              day: "numeric",
              month: "short",
              year: "numeric",
          })
        : value;
};

export const FeedbackDetail: React.FC<FeedbackDetailProps> = ({
    feedback: initialFeedback,
    onBack,
    onDelete,
    onUpdate,
}) => {
    const { config, updateFeedback, removeFeedbackItem, currentUser } =
        useMarkupStore();
    const [feedback, setFeedback] = useState(initialFeedback);
    // Follow live updates (someone else's comment, a status change elsewhere)
    // instead of freezing on the copy the ticket was opened with.
    useEffect(() => setFeedback(initialFeedback), [initialFeedback]);
    // The last write that failed, shown in place until the next attempt.
    const [actionError, setActionError] = useState<string | null>(null);
    const [teamMembers, setTeamMembers] = useState<UserInfo[]>(
        config.teamMembers ?? [],
    );
    const [linkCopied, setLinkCopied] = useState(false);
    const [newComment, setNewComment] = useState("");
    const [isAddingComment, setIsAddingComment] = useState(false);
    // The image open in the full-size viewer: the screenshot or the mockup.
    const [fullImage, setFullImage] = useState<{ src: string; alt: string } | null>(null);
    const [copied, setCopied] = useState(false);
    const [copyFailed, setCopyFailed] = useState(false);
    const [commentAdded, setCommentAdded] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
    const [archiveError, setArchiveError] = useState<string | null>(null);

    // Feature 2 & 5 states
    const [showReplay, setShowReplay] = useState(false);
    const [showOverlay, setShowOverlay] = useState(false);
    const [overlayOpacity, setOverlayOpacity] = useState(50);

    // Parse session actions
    const sessionActions = useMemo(() => {
        if (!feedback.sessionEvents) return null;
        try {
            return JSON.parse(feedback.sessionEvents);
        } catch (e) {
            console.error("Failed to parse session events", e);
            return null;
        }
    }, [feedback.sessionEvents]);

    // Format timestamp
    const formatTimestamp = (timestamp: number) => {
        const date = new Date(timestamp);
        return date.toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: false,
        });
    };

    // Get relative time
    const getRelativeTime = (timestamp: number, baseTimestamp: number) => {
        const diff = Math.floor((timestamp - baseTimestamp) / 1000);
        if (diff === 0) return "start";
        return `+${diff}s`;
    };

    const parseUserAgent = (ua: string): string => {
        const browser =
            /Edg\//.test(ua) ? "Edge" :
            /OPR\//.test(ua) ? "Opera" :
            /Chrome\//.test(ua) ? "Chrome" :
            /Firefox\//.test(ua) ? "Firefox" :
            /Safari\//.test(ua) ? "Safari" : "Browser";
        const os =
            /Windows NT/.test(ua) ? "Windows" :
            /Mac OS X/.test(ua) ? "macOS" :
            /Android/.test(ua) ? "Android" :
            /iPhone|iPad/.test(ua) ? "iOS" :
            /Linux/.test(ua) ? "Linux" : "Unknown OS";
        return `${browser} · ${os}`;
    };

    const shortenUrl = (url: string): string => {
        try {
            const u = new URL(url);
            const path = u.pathname === "/" ? "/" : u.pathname.replace(/\/$/, "");
            return u.hostname + path;
        } catch {
            return url;
        }
    };

    // Status and assignment are the dev team's to manage — decided by the
    // signed-in user's role. (This used to look at `config.user`, which is
    // unset for anyone signed in through Firebase, so every client could
    // close tickets.) `allowStatusChange: false` still turns it off for all.
    const canManage =
        config.allowStatusChange !== false &&
        canManageWorkflow(currentUser);

    const canDelete =
        currentUser
            ? canDeleteFeedback(currentUser, feedback.createdBy.id)
            : false;
    const canEdit =
        currentUser
            ? canEditFeedback(currentUser, feedback.createdBy.id)
            : false;

    // Anyone but yourself can be tagged in a comment.
    const taggable = useMemo(
        () => teamMembers.filter((m) => m.id !== currentUser?.id),
        [teamMembers, currentUser?.id],
    );

    // For the assignee picker and @-tagging.
    const signedIn = !!currentUser;
    useEffect(() => {
        if (!signedIn) return;
        let cancelled = false;
        loadTeamMembers()
            .then((members) => {
                if (cancelled) return;
                // Configured members first, then anyone else with an account.
                const known = new Set((config.teamMembers ?? []).map((m) => m.id));
                setTeamMembers([
                    ...(config.teamMembers ?? []),
                    ...members.filter((m) => !known.has(m.id)),
                ]);
            })
            .catch(() => {
                /* Picker falls back to config.teamMembers. */
            });
        return () => {
            cancelled = true;
        };
    }, [signedIn, config.teamMembers]);

    /**
     * Apply a change optimistically, write it, and put it back if the write
     * fails. Failures used to be logged and ignored: the change stayed on
     * screen, then silently reverted on the next snapshot.
     */
    const applyChange = async (
        next: FeedbackItem,
        write: () => Promise<void>,
        failure: string,
    ) => {
        const previous = feedback;
        setActionError(null);
        setFeedback(next);
        updateFeedback(next);
        try {
            await write();
            onUpdate?.(next);
            return true;
        } catch (_) {
            setFeedback(previous);
            updateFeedback(previous);
            setActionError(failure);
            return false;
        }
    };

    const handleEditSubmit = async (
        updatedFeedback: FeedbackItem,
        extras?: FeedbackExtras,
    ) => {
        // Throws back into the form on failure, which keeps the edit open.
        const stored = await storeFeedbackAssets(
            { ...updatedFeedback, id: feedback.id },
            config.projectId,
            extras?.recording,
        );
        await updateFirebaseFeedback(feedback.id, stored);
        setFeedback(stored);
        updateFeedback(stored);
        onUpdate?.(stored);
        setIsEditing(false);
    };

    const handleAssign = (memberId: string) => {
        const assignee = teamMembers.find((m) => m.id === memberId) ?? null;
        void applyChange(
            { ...feedback, assignedTo: assignee },
            () => assignFeedback(feedback.id, assignee),
            "Couldn’t update the assignee. Please try again.",
        );
    };

    const handleDueDate = (value: string) => {
        const dueDate = value || null;
        void applyChange(
            { ...feedback, dueDate },
            () => setFeedbackDueDate(feedback.id, dueDate),
            "Couldn’t update the due date. Please try again.",
        );
    };

    const handleCopyLink = async () => {
        try {
            await navigator.clipboard.writeText(buildTicketLink(feedback));
            setLinkCopied(true);
            setTimeout(() => setLinkCopied(false), 2000);
        } catch (_) {
            setActionError("Couldn’t copy the link.");
        }
    };


    if (isEditing) {
        return (
            <div className="flex flex-col h-full">
                <div className="flex items-center justify-between p-5 pb-3 mb-3 border-b border-gray-200">
                    <button type="button"
                        onClick={() => setIsEditing(false)}
                        className="flex items-center gap-1.5 text-sm! text-gray-600 bg-transparent border-none cursor-pointer hover:text-gray-900 transition-colors"
                    >
                        <ArrowLeftIcon className="w-4 h-4" />
                        Cancel Editing
                    </button>
                </div>
                <div className="flex-1 overflow-y-auto px-5">
                    <FeedbackForm
                        initialData={feedback}
                        onSubmit={handleEditSubmit}
                        onCancel={() => setIsEditing(false)}
                    />
                </div>
            </div>
        );
    }

    const handleCopy = async () => {
        const feedbackText = `
📝 ${feedback.title}
━━━━━━━━━━━━━━━━━━━━━━

${
    feedback.description ? `Description:\n${feedback.description}\n\n` : ""
}Priority: ${feedback.priority.toUpperCase()}
Status: ${feedback.status.replace("-", " ").toUpperCase()}
Created: ${formatDate(feedback.createdAt)}
Created by: ${feedback.createdBy.name}
${
    feedback.tags && feedback.tags.length > 0
        ? `\nTags: ${feedback.tags.join(", ")}`
        : ""
}
${
    feedback.pageMetadata
        ? `\n━━━━━━━━━━━━━━━━━━━━━━\nPage: ${feedback.pageMetadata.url}\nScreen: ${feedback.pageMetadata.screenWidth}x${feedback.pageMetadata.screenHeight}`
        : ""
}
${
    feedback.comments && feedback.comments.length > 0
        ? `\n━━━━━━━━━━━━━━━━━━━━━━\nComments (${
              feedback.comments.length
          }):\n${feedback.comments
              .map(
                  (c) =>
                      `• ${c.createdBy.name} (${formatDate(c.createdAt)}): ${
                          c.content || c.text
                      }`,
              )
              .join("\n")}`
        : ""
}
        `.trim();

        try {
            await navigator.clipboard.writeText(feedbackText);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (_) {
            setCopyFailed(true);
            setTimeout(() => setCopyFailed(false), 3000);
        }
    };

    const handleStatusChange = async (newStatus: FeedbackStatus) => {
        if (newStatus === feedback.status) return;
        const saved = await applyChange(
            { ...feedback, status: newStatus, updatedAt: new Date().toISOString() },
            () => updateFirebaseFeedback(feedback.id, { status: newStatus }),
            "Couldn’t change the status. Please try again.",
        );
        if (saved && config.onStatusChange) {
            await config.onStatusChange(feedback.id, newStatus);
        }
    };

    const handleAddComment = async () => {
        if (!newComment.trim()) return;

        setIsAddingComment(true);

        const text = newComment.trim();
        const mentions = resolveMentions(text, taggable);
        const comment: Comment = {
            id: generateId(),
            content: text,
            text,
            ...(mentions.length > 0 && { mentions }),
            createdAt: new Date().toISOString(),
            createdBy: currentUser || {
                id: config.user?.id || "anonymous",
                name: config.user?.name || "Anonymous",
                email: config.user?.email || "",
                role: config.user?.role || "default",
            },
        };

        const saved = await applyChange(
            {
                ...feedback,
                comments: [...(feedback.comments || []), comment],
                updatedAt: new Date().toISOString(),
            },
            () => addFeedbackComment(feedback.id, comment),
            "Couldn’t post your comment. It’s still in the box — try again.",
        );
        setIsAddingComment(false);
        if (!saved) return;

        if (config.onCommentAdd) {
            await config.onCommentAdd(feedback.id, comment);
        }
        setNewComment("");
        setCommentAdded(true);
        setTimeout(() => setCommentAdded(false), 2500);
    };

    const handleDelete = () => setShowDeleteConfirm(true);

    const handleConfirmDelete = async () => {
        setActionError(null);
        try {
            await deleteFirebaseFeedback(feedback.id);
        } catch (_) {
            // Leave it listed: it still exists.
            setActionError("Couldn’t delete this ticket. Please try again.");
            setShowDeleteConfirm(false);
            return;
        }

        if (config.onDelete) {
            await config.onDelete(feedback.id);
        }

        if (onDelete) {
            onDelete(feedback.id);
        }

        setShowDeleteConfirm(false);
    };

    const handleCancelDelete = () => setShowDeleteConfirm(false);

    const handleConfirmArchive = async () => {
        setArchiveError(null);
        try {
            await archiveFirebaseFeedback(feedback.id);
        } catch (error) {
            // Leave the ticket in place if the write failed — dropping it from
            // the store here would hide a ticket that is still live in
            // Firestore until the next refresh.
            console.error("Failed to archive feedback:", error);
            setArchiveError(
                error instanceof Error ? error.message : "Failed to archive",
            );
            return;
        }

        // Drop it locally straight away. The subscription filters archived
        // tickets out too, so this only avoids waiting for the round trip.
        removeFeedbackItem(feedback.id);
        setShowArchiveConfirm(false);
        onBack();
    };

    return (
        <div className="flex flex-col h-full px-5 overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between pb-3 mb-3 border-b border-gray-200">
                <button type="button"
                    onClick={onBack}
                    className="flex items-center gap-1.5 text-sm! text-gray-600 bg-transparent border-none cursor-pointer hover:text-gray-900 transition-colors"
                >
                    <ArrowLeftIcon className="w-4 h-4" />
                    Back
                </button>

                <div className="flex items-center gap-2">
                    <button type="button"
                        onClick={handleCopy}
                        aria-label="Copy ticket as text"
                        title="Copy as text"
                        className={`flex items-center gap-1.5 text-sm! bg-transparent border-none cursor-pointer transition-colors ${copyFailed ? "text-red-500" : "text-gray-600 hover:text-gray-900"}`}
                    >
                        {copied ? (
                            <>
                                <CheckIcon className="w-4 h-4 text-green-500" />
                                <span className="text-green-600">Copied!</span>
                            </>
                        ) : copyFailed ? (
                            <span className="text-xs!">Copy failed</span>
                        ) : (
                            <CopyIcon className="w-4 h-4" />
                        )}
                    </button>
                    {canEdit && (
                        <button type="button"
                            aria-label="Edit ticket"
                            title="Edit"
                            onClick={() => setIsEditing(true)}
                            className="flex items-center gap-1.5 text-sm! text-gray-600 bg-transparent border-none cursor-pointer hover:text-gray-900 transition-colors"
                        >
                            <EditIcon className="w-4 h-4" />
                        </button>
                    )}
                    {canEdit && (
                        <button type="button"
                            onClick={() => setShowArchiveConfirm(true)}
                            className="flex items-center gap-1.5 text-sm! text-gray-600 bg-transparent border-none cursor-pointer hover:text-gray-900 transition-colors"
                            title="Archive this ticket"
                            aria-label="Archive ticket"
                        >
                            <ArchiveIcon className="w-4 h-4" />
                        </button>
                    )}
                    {canDelete && onDelete && (
                        <button type="button"
                            aria-label="Delete ticket"
                            title="Delete"
                            onClick={handleDelete}
                            className="flex items-center gap-1.5 text-sm! text-red-600 bg-transparent border-none cursor-pointer hover:text-red-700 transition-colors"
                        >
                            <TrashIcon className="w-4 h-4" />
                        </button>
                    )}
                </div>
            </div>

            {showArchiveConfirm && (
                <div className="mb-3 p-2 rounded-xl border border-gray-200 bg-gray-50 text-gray-800">
                    <div className="flex items-center gap-3">
                        <div className="flex-1 text-xs! font-medium">
                            Archive this ticket? It'll be hidden from the list
                            and the page pins.
                        </div>
                        <button type="button"
                            onClick={() => {
                                setShowArchiveConfirm(false);
                                setArchiveError(null);
                            }}
                            className="px-2 py-1.5 text-xs! font-semibold text-gray-700 rounded-lg hover:bg-gray-200 cursor-pointer"
                        >
                            Cancel
                        </button>
                        <button type="button"
                            onClick={handleConfirmArchive}
                            className="px-2 py-1.5 text-xs! font-semibold text-white bg-gray-800 rounded-lg hover:bg-gray-900 cursor-pointer"
                        >
                            Archive
                        </button>
                    </div>
                    {archiveError && (
                        <div className="mt-2 text-xs! text-red-600">
                            {archiveError}
                        </div>
                    )}
                </div>
            )}

            {showDeleteConfirm && (
                <div className="mb-3 p-2 rounded-xl border border-red-200 bg-red-50 text-red-800 flex items-center gap-3">
                    <div className="flex-1 text-xs! font-medium">
                        Delete this feedback?
                    </div>
                    <button type="button"
                        onClick={handleCancelDelete}
                        className="px-2 py-1.5 text-xs! font-semibold text-red-700 rounded-lg hover:bg-red-100 cursor-pointer"
                    >
                        Cancel
                    </button>
                    <button type="button"
                        onClick={handleConfirmDelete}
                        className="px-2 py-1.5 text-xs! font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 cursor-pointer"
                    >
                        Delete
                    </button>
                </div>
            )}

            {/* Content */}
            <div className="flex-1 pr-1">
                {/* Title & Status */}
                <div className="mb-4">
                    <div className="flex items-start gap-2 mb-2">
                        <div className="text-base! font-semibold text-gray-900 m-0 flex-1">
                            {feedback.title}
                        </div>
                        {feedback.pageMetadata?.deviceType && (
                            <div className="flex-shrink-0 mt-0.5">
                                {feedback.pageMetadata.deviceType ===
                                    "desktop" && (
                                    <DesktopIcon
                                        className="w-5 h-5 text-gray-500"
                                        title="Desktop"
                                    />
                                )}
                                {feedback.pageMetadata.deviceType ===
                                    "tablet" && (
                                    <TabletIcon
                                        className="w-5 h-5 text-gray-500"
                                        title="Tablet"
                                    />
                                )}
                                {feedback.pageMetadata.deviceType ===
                                    "mobile" && (
                                    <MobileIcon
                                        className="w-5 h-5 text-gray-500"
                                        title="Mobile"
                                    />
                                )}
                            </div>
                        )}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        {feedback.type && (
                            <span
                                className={cn(
                                    "px-2 py-0.5 rounded-full text-[10px] font-medium uppercase",
                                    feedback.type === "copy-amendment"
                                        ? "bg-matter-ink/10 text-gray-800"
                                        : feedback.type === "bug"
                                          ? "bg-matter-accent/15 text-gray-800"
                                          : feedback.type === "feature"
                                            ? "bg-matter-ink/5 text-gray-800"
                                            : "bg-gray-100 text-gray-700",
                                )}
                            >
                                {feedback.type === "copy-amendment"
                                    ? "Copy"
                                    : "General"}
                            </span>
                        )}
                        <span
                            className={cn(
                                "px-2 py-0.5 rounded-full text-[10px] font-medium uppercase",
                                priorityColors[feedback.priority],
                            )}
                        >
                            {feedback.priority}
                        </span>
                        <span className="text-xs! text-gray-400">
                            {feedback.createdBy?.name && (
                                <>
                                    Raised by{" "}
                                    <span className="font-medium text-gray-600">
                                        {feedback.createdBy.name}
                                    </span>
                                    {" · "}
                                </>
                            )}
                            {formatDate(feedback.createdAt)}
                        </span>
                    </div>
                </div>

                {actionError && (
                    <div
                        role="alert"
                        className="mb-4 px-3 py-2 rounded-xl bg-red-50 border border-red-200 text-red-700 text-xs!"
                    >
                        {actionError}
                    </div>
                )}

                {/* Status Selector */}
                {canManage && (
                    <div className="mb-4">
                        <label className="block text-xs! font-medium text-gray-700 mb-1.5">
                            Status
                        </label>
                        <div className="flex gap-1.5">
                            {statusOptions.map((option) => (
                                <button type="button"
                                    key={option.value}
                                    onClick={() =>
                                        handleStatusChange(option.value)
                                    }
                                    className={cn(
                                        "flex-1 py-1.5 px-2 rounded-lg text-[10px] font-medium border cursor-pointer transition-all text-center",
                                        feedback.status === option.value
                                            ? cn(
                                                  option.color,
                                                  "border-transparent",
                                              )
                                            : "bg-white text-gray-600 border-gray-200 hover:border-matter-ink/40",
                                    )}
                                >
                                    {option.label}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Assignee & due date — managed by the dev team, visible to all */}
                {(canManage || feedback.assignedTo || feedback.dueDate) && (
                    <div className="grid grid-cols-2 gap-3 mb-4">
                        <div>
                            <label
                                htmlFor={`assignee-${feedback.id}`}
                                className="block text-xs! font-medium text-gray-700 mb-1.5"
                            >
                                Assignee
                            </label>
                            {canManage ? (
                                <select
                                    id={`assignee-${feedback.id}`}
                                    value={feedback.assignedTo?.id ?? ""}
                                    onChange={(e) => handleAssign(e.target.value)}
                                    className="w-full px-2.5 py-1.5 bg-white border border-gray-200 rounded-lg text-xs! text-gray-800"
                                >
                                    <option value="">Unassigned</option>
                                    {feedback.assignedTo &&
                                        !teamMembers.some((m) => m.id === feedback.assignedTo!.id) && (
                                            <option value={feedback.assignedTo.id}>
                                                {feedback.assignedTo.name}
                                            </option>
                                        )}
                                    {teamMembers.map((member) => (
                                        <option key={member.id} value={member.id}>
                                            {member.name}
                                        </option>
                                    ))}
                                </select>
                            ) : (
                                <p className="text-xs! text-gray-800 m-0">
                                    {feedback.assignedTo?.name ?? "Unassigned"}
                                </p>
                            )}
                        </div>
                        <div>
                            <label
                                htmlFor={`due-${feedback.id}`}
                                className="block text-xs! font-medium text-gray-700 mb-1.5"
                            >
                                Due
                            </label>
                            {canManage ? (
                                <input
                                    id={`due-${feedback.id}`}
                                    type="date"
                                    value={feedback.dueDate ?? ""}
                                    onChange={(e) => handleDueDate(e.target.value)}
                                    className="w-full px-2.5 py-1 bg-white border border-gray-200 rounded-lg text-xs! text-gray-800"
                                />
                            ) : (
                                <p className="text-xs! text-gray-800 m-0">
                                    {feedback.dueDate
                                        ? formatDueDate(feedback.dueDate)
                                        : "—"}
                                </p>
                            )}
                        </div>
                    </div>
                )}

                {/* Link to this ticket, for colleagues */}
                <div className="mb-4 flex items-center gap-2">
                    <button
                        type="button"
                        onClick={handleCopyLink}
                        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white border border-gray-200 text-xs! font-medium text-gray-700 hover:border-gray-300"
                    >
                        {linkCopied ? (
                            <CheckIcon className="w-3.5 h-3.5 text-green-600" />
                        ) : (
                            <CopyIcon className="w-3.5 h-3.5" />
                        )}
                        {linkCopied ? "Link copied" : "Copy link"}
                    </button>
                    <span className="text-[11px] text-gray-500">
                        Opens this ticket on its page for anyone at Matter.
                    </span>
                </div>

                {/* Screenshot */}
                {feedback.screenshot && (
                    <div className="mb-5">
                        <label className="block text-xs! font-semibold text-gray-800 mb-2 uppercase tracking-wide">
                            Screenshot
                        </label>
                        <button type="button"
                            aria-label="View full-size screenshot"
                            className="relative w-full block rounded-xl overflow-hidden shadow-md cursor-pointer group"
                            onClick={() =>
                                setFullImage({ src: feedback.screenshot!, alt: "Feedback screenshot" })
                            }
                        >
                            <img
                                src={feedback.screenshot}
                                alt="Feedback screenshot"
                                className="w-full block"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-all flex items-center justify-center">
                                <div className="bg-white rounded-full p-3 shadow-xl transform scale-90 group-hover:scale-100 transition-transform">
                                    <ZoomInIcon className="w-6 h-6 text-gray-700" />
                                </div>
                            </div>
                        </button>
                    </div>
                )}

                {/* Design Mockup */}
                {feedback.designMockup && (
                    <div className="mb-5">
                        <label className="block text-xs! font-semibold text-gray-800 mb-2 uppercase tracking-wide">
                            Design Mockup
                        </label>
                        <button type="button"
                            aria-label="View full-size mockup"
                            className="relative w-full block rounded-xl overflow-hidden shadow-md cursor-pointer group"
                            onClick={() =>
                                setFullImage({ src: feedback.designMockup!, alt: "Design mockup" })
                            }
                        >
                            <img
                                src={feedback.designMockup}
                                alt="Design mockup"
                                className="w-full block"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-all flex items-center justify-center">
                                <div className="bg-white rounded-full p-3 shadow-xl transform scale-90 group-hover:scale-100 transition-transform">
                                    <ZoomInIcon className="w-6 h-6 text-gray-700" />
                                </div>
                            </div>
                        </button>
                    </div>
                )}

                {/* Screen Recording */}
                {feedback.screenRecording && (
                    <div className="mb-5">
                        <label className="block text-xs! font-semibold text-gray-800 mb-2 uppercase tracking-wide">
                            Screen Recording
                        </label>
                        <div className="relative rounded-xl overflow-hidden shadow-md border border-gray-200">
                            <video
                                src={feedback.screenRecording}
                                controls
                                className="w-full h-auto bg-black"
                            />
                        </div>
                    </div>
                )}

                {/* Advanced Features */}
                {(feedback.designMockup || feedback.sessionEvents) && (
                    <div className="flex gap-2 mb-5">
                        {feedback.sessionEvents && (
                            <button type="button"
                                onClick={() => setShowReplay(true)}
                                className="flex-1 bg-gray-100 text-gray-700 py-2.5 rounded-xl text-xs! font-semibold hover:bg-gray-200 transition-colors flex items-center justify-center gap-2 border border-gray-200 cursor-pointer"
                            >
                                Play Session
                            </button>
                        )}
                        {feedback.designMockup && (
                            <button type="button"
                                onClick={() => setShowOverlay(true)}
                                className="flex-1 bg-gray-100 text-gray-700 py-2.5 rounded-xl text-xs! font-semibold hover:bg-gray-200 transition-colors flex items-center justify-center gap-2 border border-gray-200 cursor-pointer"
                            >
                                Overlay Mockup
                            </button>
                        )}
                    </div>
                )}

                {/* Copy Amendment Fields */}
                {feedback.type === "copy-amendment" && (
                    <>
                        {feedback.currentCopy && (
                            <div className="mb-4">
                                <label className="block text-xs! font-medium text-gray-700 mb-1.5">
                                    Current Copy
                                </label>
                                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                                    <p className="text-sm! text-gray-800 m-0 whitespace-pre-wrap">
                                        {feedback.currentCopy}
                                    </p>
                                </div>
                            </div>
                        )}
                        {feedback.newCopy && (
                            <div className="mb-4">
                                <label className="block text-xs! font-medium text-gray-700 mb-1.5">
                                    New Copy
                                </label>
                                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                                    <p className="text-sm! text-gray-800 m-0 whitespace-pre-wrap">
                                        {feedback.newCopy}
                                    </p>
                                </div>
                            </div>
                        )}
                    </>
                )}

                {/* Image Change Fields */}
                {feedback.type === "image-change" && (
                    <>
                        {(feedback.originalImageSrc ||
                            feedback.originalImageBackground) && (
                            <div className="mb-4">
                                <label className="block text-xs! font-medium text-gray-700 mb-1.5">
                                    Current Image
                                </label>
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                    {feedback.originalImageSrc ? (
                                        <>
                                            <img
                                                src={feedback.originalImageSrc}
                                                alt="Original"
                                                className="w-full h-48 object-cover rounded-lg mb-2"
                                            />
                                            <p className="text-[10px] text-gray-600 font-mono break-all">
                                                {feedback.originalImageSrc}
                                            </p>
                                        </>
                                    ) : (
                                        <p className="text-[10px] text-gray-600 font-mono break-all">
                                            Background Image:{" "}
                                            {feedback.originalImageBackground}
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}
                        {feedback.replacementImageUrl && (
                            <div className="mb-4">
                                <label className="block text-xs! font-medium text-gray-700 mb-1.5">
                                    Replacement Image
                                </label>
                                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                                    <img
                                        src={feedback.replacementImageUrl}
                                        alt="Replacement"
                                        className="w-full h-48 object-cover rounded-lg mb-2"
                                    />
                                    <p className="text-[10px] text-gray-600 font-mono break-all">
                                        {feedback.replacementImageUrl}
                                    </p>
                                </div>
                            </div>
                        )}
                    </>
                )}

                {/* Description */}
                {feedback.description && (
                    <div className="mb-4">
                        <label className="block text-xs! font-medium text-gray-700 mb-1.5">
                            Description
                        </label>
                        <p className="text-sm! text-gray-600 m-0 whitespace-pre-wrap">
                            {feedback.description}
                        </p>
                    </div>
                )}

                {/* Tags */}
                {feedback.tags && feedback.tags.length > 0 && (
                    <div className="mb-4">
                        <label className="block text-xs! font-medium text-gray-700 mb-1.5">
                            Tags
                        </label>
                        <div className="flex flex-wrap gap-1.5">
                            {feedback.tags.map((tag) => (
                                <span
                                    key={tag}
                                    className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs!"
                                >
                                    {tag}
                                </span>
                            ))}
                        </div>
                    </div>
                )}

                {/* Page Info */}
                {feedback.pageMetadata && (
                    <div className="mb-4 p-2.5 bg-gray-50 rounded-lg">
                        <label className="block text-xs! font-medium text-gray-700 mb-1.5">
                            Page Info
                        </label>
                        <div className="text-xs! text-gray-500 space-y-1">
                            <div className="truncate" title={feedback.pageMetadata.url}>
                                <span className="font-medium">URL:</span>{" "}
                                {shortenUrl(feedback.pageMetadata.url)}
                            </div>
                            {feedback.pageMetadata.componentName && (
                                <div className="truncate">
                                    <span className="font-medium">
                                        Component:
                                    </span>{" "}
                                    <code className="bg-gray-200 px-1.5 py-0.5 rounded text-xs! font-mono text-gray-800">
                                        {feedback.pageMetadata.componentName}
                                    </code>
                                </div>
                            )}
                            {feedback.pageMetadata.elementInfo && (
                                <div className="truncate">
                                    <span className="font-medium">
                                        Element:
                                    </span>{" "}
                                    <code className="bg-blue-50 px-1.5 py-0.5 rounded text-xs! font-mono text-blue-800">
                                        {feedback.pageMetadata.elementInfo}
                                    </code>
                                </div>
                            )}
                            <div>
                                <span className="font-medium">Screen:</span>{" "}
                                {feedback.pageMetadata.screenWidth}x
                                {feedback.pageMetadata.screenHeight}
                            </div>
                            <div title={feedback.pageMetadata.userAgent}>
                                <span className="font-medium">Browser:</span>{" "}
                                {parseUserAgent(feedback.pageMetadata.userAgent)}
                            </div>
                        </div>
                    </div>
                )}

                {/* Comments */}
                <div className="mb-4">
                    <label className="block text-xs! font-medium text-gray-700 mb-2">
                        Comments ({feedback.comments?.length || 0})
                    </label>

                    {feedback.comments && feedback.comments.length > 0 && (
                        <div className="space-y-3 mb-4">
                            {feedback.comments.map((comment) => (
                                <div
                                    key={comment.id}
                                    className={cn(
                                        "p-3 bg-white border rounded-xl shadow-sm",
                                        currentUser && commentMentions(comment, currentUser.id)
                                            ? "border-matter-accent/40"
                                            : "border-gray-200",
                                    )}
                                >
                                    <div className="flex items-center gap-2 mb-2">
                                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-matter-ink to-matter-accent flex items-center justify-center shadow-md">
                                            <UserIcon className="w-4 h-4 text-white" />
                                        </div>
                                        <span className="text-xs! font-semibold text-gray-900">
                                            {comment.createdBy.name}
                                        </span>
                                        <span className="text-[10px] text-gray-500 font-medium">
                                            {formatDate(comment.createdAt)}
                                        </span>
                                    </div>
                                    <p className="text-sm! text-gray-700 m-0 ml-10 whitespace-pre-wrap">
                                        {splitMentions(
                                            comment.content || comment.text || "",
                                            comment.mentions,
                                        ).map((part, i) =>
                                            part.mention ? (
                                                <span
                                                    key={i}
                                                    title={part.mention.email}
                                                    className={cn(
                                                        "font-semibold rounded px-0.5",
                                                        part.mention.id === currentUser?.id
                                                            ? "bg-matter-accent/15 text-matter-ink"
                                                            : "text-matter-ink",
                                                    )}
                                                >
                                                    {part.text}
                                                </span>
                                            ) : (
                                                <React.Fragment key={i}>{part.text}</React.Fragment>
                                            ),
                                        )}
                                    </p>
                                </div>
                            ))}
                        </div>
                    )}

                    {commentAdded && (
                        <div className="flex items-center gap-1.5 mb-3 px-3 py-2 bg-green-50 border border-green-200 rounded-xl text-xs! text-green-700 font-medium">
                            <CheckIcon className="w-3.5 h-3.5" />
                            Comment added
                        </div>
                    )}

                    {/* Add Comment */}
                    <div className="flex gap-2">
                        <CommentInput
                            value={newComment}
                            onChange={setNewComment}
                            onSubmit={handleAddComment}
                            members={taggable}
                        />
                        <button
                            type="button"
                            onClick={handleAddComment}
                            disabled={!newComment.trim() || isAddingComment}
                            // A class, not an inline background: the widget's
                            // button reset overrides inline backgrounds.
                            className="p-2.5 rounded-xl border-none bg-matter-ink text-matter-cream cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg transition-all flex items-center justify-center"
                            aria-label="Send comment"
                        >
                            {isAddingComment ? (
                                <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            ) : (
                                <SendIcon className="w-5 h-5" />
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {/* Full Screenshot Modal */}
            {fullImage &&
                <WidgetPortal>
                    <div
                        className="fixed inset-0 bg-black/90 backdrop-blur-sm z-[2147483647] flex items-center justify-center p-10 animate-in fade-in duration-200"
                        onClick={() => setFullImage(null)}
                    >
                        <button
                            onClick={() => setFullImage(null)}
                            className="absolute top-6 right-6 p-2 bg-white/10 rounded-full border-none cursor-pointer hover:bg-white/20 transition-colors z-10"
                        >
                            <CloseIcon className="w-8 h-8 text-white" />
                        </button>
                        <img
                            src={fullImage.src}
                            alt={fullImage.alt}
                            className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
                            onClick={(e) => e.stopPropagation()}
                        />
                    </div></WidgetPortal>}

            {/* Session Action Timeline Modal */}
            {showReplay &&
                sessionActions &&
                sessionActions.length > 0 &&
                <WidgetPortal>
                    <div
                        className="fixed inset-0 z-[10000] bg-black/80 backdrop-blur-sm flex items-center justify-center p-5 animate-in fade-in duration-200"
                        onClick={() => setShowReplay(false)}
                    >
                        <div
                            className="bg-white rounded-2xl overflow-hidden shadow-2xl max-w-[900px] w-full flex flex-col max-h-[90vh]"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="p-4 border-b border-gray-100 flex items-center justify-between bg-gray-50">
                                <div>
                                    <div className="font-semibold text-gray-900">
                                        Session Activity Timeline
                                    </div>
                                    <p className="text-xs! text-gray-600 mt-1">
                                        User actions recorded during feedback
                                        creation
                                    </p>
                                </div>
                                <button
                                    onClick={() => setShowReplay(false)}
                                    className="p-2 hover:bg-gray-200 rounded-full transition-colors cursor-pointer text-gray-500 hover:text-gray-900 border-none bg-transparent"
                                >
                                    <CloseIcon className="w-5 h-5" />
                                </button>
                            </div>
                            <div className="flex-1 overflow-y-auto p-4">
                                <div className="space-y-2">
                                    {sessionActions.map(
                                        (action: any, idx: number) => {
                                            const baseTime =
                                                sessionActions[0]?.timestamp ||
                                                action.timestamp;

                                            return (
                                                <div
                                                    key={idx}
                                                    className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                                                >
                                                    <div className="flex-shrink-0 w-20 text-xs! font-mono text-gray-600">
                                                        <div>
                                                            {formatTimestamp(
                                                                action.timestamp,
                                                            )}
                                                        </div>
                                                        <div className="text-gray-400">
                                                            {getRelativeTime(
                                                                action.timestamp,
                                                                baseTime,
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        {action.type ===
                                                            "click" && (
                                                            <div>
                                                                <div className="flex items-center gap-2 mb-1">
                                                                    <span className="inline-block w-2 h-2 bg-blue-500 rounded-full"></span>
                                                                    <span className="font-medium text-gray-900">
                                                                        Clicked
                                                                    </span>
                                                                </div>
                                                                <div className="text-sm! text-gray-700 font-mono bg-white px-2 py-1 rounded border border-gray-200">
                                                                    &lt;
                                                                    {action.data
                                                                        .elementTag ||
                                                                        "unknown"}
                                                                    {action.data
                                                                        .elementId &&
                                                                        ` id="${action.data.elementId}"`}
                                                                    {action.data
                                                                        .elementClass &&
                                                                        ` class="${action.data.elementClass}"`}
                                                                    &gt;
                                                                </div>
                                                                {action.data
                                                                    .elementText && (
                                                                    <div className="mt-1 text-sm! text-gray-600">
                                                                        Text:{" "}
                                                                        <span className="italic">
                                                                            "
                                                                            {action.data.elementText.substring(
                                                                                0,
                                                                                100,
                                                                            )}
                                                                            {action
                                                                                .data
                                                                                .elementText
                                                                                .length >
                                                                            100
                                                                                ? "..."
                                                                                : ""}
                                                                            "
                                                                        </span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                        {action.type ===
                                                            "navigation" && (
                                                            <div>
                                                                <div className="flex items-center gap-2 mb-1">
                                                                    <span className="inline-block w-2 h-2 bg-matter-accent rounded-full"></span>
                                                                    <span className="font-medium text-gray-900">
                                                                        Navigated
                                                                    </span>
                                                                </div>
                                                                <div className="text-sm! text-gray-700 break-all">
                                                                    {
                                                                        action
                                                                            .data
                                                                            .url
                                                                    }
                                                                </div>
                                                            </div>
                                                        )}
                                                        {action.type ===
                                                            "scroll" && (
                                                            <div>
                                                                <div className="flex items-center gap-2 mb-1">
                                                                    <span className="inline-block w-2 h-2 bg-green-500 rounded-full"></span>
                                                                    <span className="font-medium text-gray-900">
                                                                        Scrolled
                                                                    </span>
                                                                </div>
                                                                <div className="text-sm! text-gray-700">
                                                                    Position:{" "}
                                                                    {
                                                                        action
                                                                            .data
                                                                            .scrollY
                                                                    }
                                                                    px
                                                                </div>
                                                            </div>
                                                        )}
                                                        {action.type ===
                                                            "input" && (
                                                            <div>
                                                                <div className="flex items-center gap-2 mb-1">
                                                                    <span className="inline-block w-2 h-2 bg-orange-500 rounded-full"></span>
                                                                    <span className="font-medium text-gray-900">
                                                                        Input
                                                                    </span>
                                                                </div>
                                                                <div className="text-sm! text-gray-700">
                                                                    {
                                                                        action
                                                                            .data
                                                                            .inputType
                                                                    }{" "}
                                                                    field
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        },
                                    )}
                                </div>
                            </div>
                        </div>
                    </div></WidgetPortal>}

            {/* Mockup Overlay */}
            {showOverlay &&
                feedback.designMockup &&
                <WidgetPortal>
                    <div className="fixed inset-0 z-[10000] pointer-events-none">
                        <img
                            src={feedback.designMockup}
                            style={{ opacity: overlayOpacity / 100 }}
                            className="w-full h-full object-contain object-top"
                            alt="Design Mockup Overlay"
                        />
                        <div className="fixed bottom-10 left-1/2 -translate-x-1/2 bg-white/90 backdrop-blur border border-gray-200 shadow-xl rounded-full p-2 px-6 flex items-center gap-4 pointer-events-auto">
                            <span className="text-xs! font-bold text-gray-500 uppercase whitespace-nowrap">
                                Overlay Opacity
                            </span>
                            <input
                                type="range"
                                min="0"
                                max="100"
                                value={overlayOpacity}
                                onChange={(e) =>
                                    setOverlayOpacity(Number(e.target.value))
                                }
                                className="w-32 accent-matter-ink"
                            />
                            <button
                                onClick={() => setShowOverlay(false)}
                                className="ml-2 hover:bg-gray-200 p-1.5 rounded-full border-none bg-transparent"
                            >
                                <CloseIcon className="w-4 h-4 text-gray-500" />
                            </button>
                        </div>
                    </div></WidgetPortal>}
        </div>
    );
};

export default FeedbackDetail;
